import React, {useEffect, useState} from 'react';
import {ChevronDownIcon} from '../icons';

export interface ItemFormSectionProps {
    /** Ключ секции — основа id заголовка и панели. */
    id: string;
    title: string;
    /** Короткая подпись справа: что уже заполнено в свёрнутой секции. */
    summary?: string;
    open: boolean;
    onToggle: () => void;
    children: React.ReactNode;
}

/**
 * Сворачивание — 200 мс (`motion.panel.exit`), раскрытие — 250 мс
 * (`motion.panel.enter`): выход всегда быстрее входа, решение уже принято.
 */
const COLLAPSE_MS = 200;

/**
 * Секция формы вещи — аккордеон.
 *
 * Motion: единственная анимация, которой в дизайн-системе разрешено стоить
 * layout — раскрытие по высоте (у неё нет transform-эквивалента). Высоту даёт
 * `grid-template-rows: 0fr → 1fr`, поэтому измерять контент в JS не нужно.
 * 200 мс `ease-out`, свёрнутая панель уходит из дерева доступности и из порядка
 * табуляции (`hidden`) после завершения анимации. `prefers-reduced-motion`
 * обрабатывается централизованно в `index.css`: у элементов с `transition`
 * остаются только opacity и цвет, поэтому раскрытие становится мгновенным.
 */
const ItemFormSection: React.FC<ItemFormSectionProps> = ({id, title, summary, open, onToggle, children}) => {
    // `mounted` держит панель в дереве доступности, `grown` тянет высоту.
    const [mounted, setMounted] = useState(open);
    const [grown, setGrown] = useState(open);

    useEffect(() => {
        if (open) {
            setMounted(true);
            // Высоту меняем со следующего кадра — иначе перехода из 0fr не будет.
            const frame = requestAnimationFrame(() => setGrown(true));
            return () => cancelAnimationFrame(frame);
        }
        setGrown(false);
        const timer = setTimeout(() => setMounted(false), COLLAPSE_MS);
        return () => clearTimeout(timer);
    }, [open]);

    return (
        <section
            className="rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-ink-800"
            data-testid={`item-form-section-${id}`}
        >
            <h3>
                <button
                    type="button"
                    id={`${id}-header`}
                    aria-expanded={open}
                    aria-controls={`${id}-panel`}
                    onClick={onToggle}
                    className="w-full min-h-[44px] flex items-center justify-between gap-3 px-4 py-3 text-left
                               rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500/40
                               hover:bg-gray-50 dark:hover:bg-white/5 transition-colors duration-ui ease-out"
                >
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{title}</span>
                    <span className="flex items-center gap-2 shrink-0">
                        {summary && (
                            <span className="text-xs text-gray-500 dark:text-gray-400 max-w-[12rem] truncate">
                                {summary}
                            </span>
                        )}
                        <ChevronDownIcon
                            className={`w-5 h-5 text-gray-400 transition-transform duration-ui ease-out ${
                                open ? 'rotate-180' : ''
                            }`}
                        />
                    </span>
                </button>
            </h3>
            <div
                className={`grid transition-[grid-template-rows] ease-out ${
                    open ? 'duration-panel' : 'duration-exit'
                } ${grown ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}
            >
                <div
                    id={`${id}-panel`}
                    role="region"
                    aria-labelledby={`${id}-header`}
                    hidden={!mounted}
                    className="overflow-hidden"
                >
                    <div className="px-4 pb-4 space-y-4">{children}</div>
                </div>
            </div>
        </section>
    );
};

export default ItemFormSection;
